import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';

// Logo inline for login page
const Eye = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
const EyeOff = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>;
const Sun = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>;
const Moon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>;

export default function LoginPage() {
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email:'', password:'' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try { await login(form.email, form.password); navigate('/'); }
    catch(err) { setError(err.response?.data?.message || 'Server error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background:'var(--bg)' }}>
      <button onClick={toggleTheme} className="fixed top-4 right-4 p-2 rounded-lg transition-colors" style={{ background:'var(--surface)', border:'1px solid var(--border)', color:'var(--text-secondary)' }}>
        {theme === 'dark' ? <Sun /> : <Moon />}
      </button>
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 flex justify-center">
            <svg width="64" height="64" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Q-Lana TMS">
              <rect width="40" height="40" rx="9" fill="#1A2F5E"/>
              <line x1="9" y1="13" x2="27" y2="13" stroke="white" strokeWidth="3" strokeLinecap="round"/>
              <line x1="9" y1="20" x2="27" y2="20" stroke="white" strokeWidth="3" strokeLinecap="round"/>
              <line x1="9" y1="27" x2="20" y2="27" stroke="white" strokeWidth="3" strokeLinecap="round"/>
              <polyline points="21,24 25,28 33,18" stroke="#5CC3BC" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </svg>
          </div>
          <h1 className="text-3xl font-bold" style={{ color:'var(--text)' }}>Task Management System</h1>
          <p className="text-sm mt-1" style={{ color:'var(--text-secondary)' }}>Implementation & Training Management</p>
        </div>
        <div className="card shadow-xl">
          <h2 className="font-semibold mb-5" style={{ color:'var(--text)' }}>Sign in to continue</h2>
          {error && <div className="mb-4 p-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-600 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><label className="label">Email</label><input type="email" className="input" placeholder="you@company.com" value={form.email} onChange={e => setForm(f=>({...f,email:e.target.value}))} required /></div>
            <div>
              <label className="label">Password</label>
              <div className="relative">
                <input type={showPw?'text':'password'} className="input pr-10" placeholder="••••••••" value={form.password} onChange={e => setForm(f=>({...f,password:e.target.value}))} required />
                <button type="button" onClick={() => setShowPw(s=>!s)} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color:'var(--text-secondary)' }}>{showPw?<EyeOff/>:<Eye/>}</button>
              </div>
            </div>
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs hover:underline" style={{ color:'var(--text-secondary)' }}>
                Forgot password?
              </Link>
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2 py-2.5">
              {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : '→'} Sign In
            </button>
          </form>

        </div>
        <p className="text-center text-xs mt-6" style={{ color:'var(--text-secondary)' }}>&copy; Q-Lana Inc. &middot; All rights reserved</p>
      </div>
    </div>
  );
}
